import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Archive, ArchiveRestore, Copy, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { fmtDate, useBriefListActions, useBriefs, type BriefListItem } from "@/lib/briefs";

function StatusBadge({ b }: { b: BriefListItem }) {
  if (b.submissionCount === 0) {
    return <span className="bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-[11px]">Draft</span>;
  }
  return (
    <span className="bg-accent text-accent-foreground rounded-full px-2 py-0.5 text-[11px]">
      Submitted v{b.submissionCount}
      {b.editedSinceSubmit ? " · edited since" : ""}
    </span>
  );
}

function Card({ b, archived }: { b: BriefListItem; archived: boolean }) {
  const { duplicate, archive, remove } = useBriefListActions();
  const navigate = useNavigate();
  const busy = duplicate.isPending || archive.isPending || remove.isPending;
  return (
    <li className="bg-card border-border rounded-lg border p-4 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/briefs/${b.id}`} className="block truncate text-base font-semibold hover:underline">
            {b.title}
          </Link>
          <p className="text-muted-foreground mt-0.5 truncate text-sm">
            {b.summary || "No summary yet."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {b.ownerUnseen ? <span className="bg-primary h-2 w-2 rounded-full" aria-label="New update" /> : null}
          <StatusBadge b={b} />
        </div>
      </div>
      <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          {b.clear} of {b.core} clear
        </span>
        {b.adminLabel ? <span className="text-foreground">{b.adminLabel}</span> : null}
        <span>Updated {fmtDate(b.updatedAt)}</span>
        <span className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => duplicate.mutate(b.id, { onSuccess: ({ brief }) => navigate(`/briefs/${brief.id}`) })} aria-label="Duplicate">
            <Copy size={14} /> Duplicate
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => archive.mutate({ id: b.id, archived: !archived })}>
            {archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            {archived ? "Restore" : "Archive"}
          </Button>
          {b.submissionCount === 0 ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Delete "${b.title}"? This cannot be undone.`)) remove.mutate(b.id);
              }}
            >
              <Trash2 size={14} /> Delete
            </Button>
          ) : null}
        </span>
      </div>
    </li>
  );
}

export function MyBriefs() {
  const [scope, setScope] = useState<"active" | "archived">("active");
  const briefs = useBriefs(scope);
  const { create } = useBriefListActions();
  const navigate = useNavigate();

  return (
    <main className="mx-auto max-w-4xl px-4 py-7">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">My briefs</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            One page, six boxes. Tell us what you want, or what you already have, and we work out the rest together.
          </p>
        </div>
        <Button
          size="lg"
          disabled={create.isPending}
          onClick={() => create.mutate(undefined, { onSuccess: ({ brief }) => navigate(`/briefs/${brief.id}`) })}
        >
          <Plus size={16} /> New brief
        </Button>
      </div>

      <div className="mb-4 flex gap-1" role="tablist">
        {(["active", "archived"] as const).map((s) => (
          <button
            key={s}
            role="tab"
            aria-selected={scope === s}
            onClick={() => setScope(s)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm",
              scope === s ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {s === "active" ? "Active" : "Archived"}
          </button>
        ))}
      </div>

      {briefs.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : briefs.isError ? (
        <p className="text-destructive text-sm">Could not load your briefs. Try again in a moment.</p>
      ) : briefs.data.length === 0 ? (
        <div className="border-border rounded-lg border border-dashed p-10 text-center">
          <p className="font-medium">{scope === "active" ? "No briefs yet." : "Nothing archived."}</p>
          {scope === "active" ? (
            <p className="text-muted-foreground mt-1 text-sm">
              Start one. One sentence about what wastes your team's time is enough.
            </p>
          ) : null}
        </div>
      ) : (
        <ul className="space-y-3">
          {briefs.data.map((b) => (
            <Card key={b.id} b={b} archived={scope === "archived"} />
          ))}
        </ul>
      )}
    </main>
  );
}
